import React from 'react';

import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

/**
 * @description
 * 브라우저 언어를 감지하여 적절한 언어 경로로 리다이렉트하는 컴포넌트입니다.
 * 현재 경로에 언어 코드가 없고, 브라우저 언어가 베트남어(vi) 또는 중국어(cn)인 경우 해당 언어 경로로 리다이렉트합니다.
 * 그 외의 언어는 모두 영어로 처리됩니다.
 *
 * @example
 * 중국어 사용자가 "/listing" 접속 → "/cn/listing"으로 리다이렉트
 * 베트남어 사용자가 "/my" 접속 → "/vi/my"로 리다이렉트
 * 영어/기타 언어 사용자가 "/listing" 접속 → 그대로 "/listing" 유지
 *
 * @returns {JSX.Element} 리다이렉트가 필요한 경우 Navigate 컴포넌트, 그렇지 않으면 Outlet
 */
export const LanguageRedirect = () => {
  const { i18n } = useTranslation();

  const location = useLocation();

  const supportedLanguages = ['vi', 'zh', 'ja'];
  const pathSegments = location.pathname.split('/');
  const currentPathLanguage = pathSegments[1];

  // 이미 언어 경로가 있는 경우 처리하지 않음
  if (supportedLanguages.includes(currentPathLanguage)) {
    return <Outlet />;
  }

  // // 브라우저 언어 감지
  const browserLanguage = i18n.language;
  const detectedLanguage = supportedLanguages.includes(browserLanguage) ? browserLanguage : null;

  // 베트남어 또는 중국어가 감지된 경우 해당 언어 경로로 리다이렉트
  if (detectedLanguage) {
    const newPath = `/${detectedLanguage}${location.pathname}`;
    return <Navigate to={newPath} replace />;
  }

  return <Outlet />;
};

/**
 * @description
 * 언어별 경로를 처리하는 래퍼 컴포넌트입니다.
 * URL에서 언어 파라미터를 추출하고, 지원하지 않는 언어인 경우 404 페이지로 리다이렉트합니다.
 * 영어(en)는 기본 언어이므로 언어 경로로 처리하지 않습니다.
 *
 * @example
 * 지원하는 언어: vi(베트남어), zh(중국어) (en은 기본 언어로 언어 경로 없음)
 * Route path="/:lang" element={LanguageWrapper}에서 사용
 *
 * @returns {JSX.Element} 지원하는 언어인 경우 Outlet을 렌더링, 그렇지 않으면 404 페이지로 리다이렉트
 */

export const LanguageWrapper = () => {
  const { lang } = useParams<{ lang: string }>();

  if (lang && !['vi', 'zh', 'ja'].includes(lang)) {
    return <Navigate to="/404" replace />;
  }

  return <Outlet />;
};

/**
 * @description
 * 언어별 경로에서 404 페이지로 리다이렉트하는 컴포넌트입니다.
 * 현재 언어 경로를 유지하면서 해당 언어의 404 페이지로 이동합니다.
 *
 * @example
 * /vi/invalid-page 접근 시 → /vi/404로 리다이렉트
 * /cn/invalid-page 접근 시 → /cn/404로 리다이렉트
 *
 * @returns {JSX.Element} 현재 언어의 404 페이지로 리다이렉트하는 Navigate 컴포넌트
 */
export const LangNotFoundRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  return <Navigate to={`/${lang}/404`} replace />;
};
